import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, addDays, subDays, parseISO } from "date-fns";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Printer,
  Save,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DateWithDataPicker } from "@/components/DateWithDataPicker";
import { ExportButton } from "@/components/ExportButton";
import LVMDashboard from "@/pages/LVMDashboard";
import { useFlushOnHide, keepaliveSave } from "@/hooks/useFlushOnHide";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display order matches the user's screenshot exactly: $1, $5, $10, $20, $50, $100, $2.
const LVM_DENOMS = [
  { key: "1", label: "$1", denom: 1 },
  { key: "5", label: "$5", denom: 5 },
  { key: "10", label: "$10", denom: 10 },
  { key: "20", label: "$20", denom: 20 },
  { key: "50", label: "$50", denom: 50 },
  { key: "100", label: "$100", denom: 100 },
  { key: "2", label: "$2", denom: 2 },
];

const REPORT_FIELDS = ["olTotal", "instTotal", "online", "instant", "creditCard", "coupon"];

const EMPTY_SECTION = {
  cash: Object.fromEntries(LVM_DENOMS.map((d) => [d.key, ""])),
  reports: Object.fromEntries(REPORT_FIELDS.map((f) => [f, ""])),
};

const cleanInt = (v) => String(v ?? "").replace(/[^0-9]/g, "");
const cleanDecimal = (v) => {
  const s = String(v ?? "").replace(/[^0-9.]/g, "");
  // collapse multiple dots → keep only the first
  const i = s.indexOf(".");
  if (i === -1) return s;
  return s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, "");
};
const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const fmtMoney = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Sum( count × denom ) over all bill rows.
const calcTotalCash = (cash) =>
  LVM_DENOMS.reduce((sum, d) => sum + toNum(cash?.[d.key]) * d.denom, 0);

// Total SLS = O/L Total + INST Total
const calcTotalSLS = (reports) =>
  toNum(reports?.olTotal) + toNum(reports?.instTotal);

// Cash In Hand = Total SLS − (Online + Instant + Credit Card + Coupon)
const calcCashInHand = (reports) =>
  calcTotalSLS(reports) -
  (toNum(reports?.online) +
    toNum(reports?.instant) +
    toNum(reports?.creditCard) +
    toNum(reports?.coupon));

// ────────────────────────────────────────────────────────────────────────────
// Single LVM section (LVM-1 or LVM-2). Mirrors the screenshot layout: two
// stacked columns (cash count on left, reports on right) inside one card.
//
// tabOffset assigns the data-lvm-tab attribute to all inputs so the global
// Enter-to-next handler in LVM() can walk through every input in order:
//   LVM-1: cash 1-7, reports 8-13
//   LVM-2: cash 14-20, reports 21-26
// ────────────────────────────────────────────────────────────────────────────
function LVMSection({ title, section, onChange, dateStr, testIdPrefix, tabOffset = 0 }) {
  const totalCash = useMemo(() => calcTotalCash(section.cash), [section.cash]);
  const totalSLS = useMemo(() => calcTotalSLS(section.reports), [section.reports]);
  const cashInHand = useMemo(() => calcCashInHand(section.reports), [section.reports]);
  const mismatch = Math.round(totalCash) !== Math.round(cashInHand);

  const updateCash = (key, v) =>
    onChange({ ...section, cash: { ...section.cash, [key]: cleanInt(v) } });
  const updateReport = (key, v) =>
    onChange({ ...section, reports: { ...section.reports, [key]: cleanDecimal(v) } });

  return (
    <Card className="border-0 shadow-lg flex-1 min-w-0" data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="py-2 px-3 bg-emerald-700/40 border-b border-emerald-500/40 rounded-t-md">
        <CardTitle className="font-mono text-center text-base text-emerald-50 tracking-wider">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        {/* Date strip */}
        <div className="flex items-center gap-2 mb-3 text-xs font-mono">
          <span className="text-muted-foreground">Date</span>
          <span className="bg-[#ece2cd] text-black px-2 py-1 rounded font-bold" data-testid={`${testIdPrefix}-date`}>
            {format(parseISO(dateStr), "M/d/yy")}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* LEFT: Cash count by denomination */}
          <div className="space-y-1">
            {LVM_DENOMS.map((d, cashIdx) => {
              const cnt = toNum(section.cash[d.key]);
              const lineTotal = cnt * d.denom;
              return (
                <div key={d.key} className="grid grid-cols-[2.25rem_minmax(0,1fr)_minmax(0,1fr)] items-center gap-1">
                  <div className="text-primary font-mono font-bold text-sm text-center bg-secondary/40 rounded h-7 flex items-center justify-center">
                    {d.label}
                  </div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={section.cash[d.key]}
                    onChange={(e) => updateCash(d.key, e.target.value)}
                    placeholder="0"
                    className="h-7 text-center text-sm font-mono p-0 rounded-md"
                    data-testid={`${testIdPrefix}-cash-${d.key}`}
                    data-lvm-tab={tabOffset + cashIdx + 1}
                  />
                  <div
                    className="h-7 flex items-center justify-end pr-1 text-sm font-mono bg-secondary/30 rounded text-card-foreground"
                    data-testid={`${testIdPrefix}-cash-val-${d.key}`}
                  >
                    {lineTotal > 0 ? fmtMoney(lineTotal) : <span className="text-muted-foreground/60">$ -</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT: Reports */}
          <div className="space-y-1">
            <ReportRow label="O/L Total" value={section.reports.olTotal} onChange={(v) => updateReport("olTotal", v)} testId={`${testIdPrefix}-r-olTotal`} tab={tabOffset + 8} />
            <ReportRow label="INST Total" value={section.reports.instTotal} onChange={(v) => updateReport("instTotal", v)} testId={`${testIdPrefix}-r-instTotal`} tab={tabOffset + 9} />
            <ComputedRow label="Total SLS" value={totalSLS} testId={`${testIdPrefix}-totalSLS`} highlight />

            {/* Prizes Redeemed group */}
            <div className="mt-2 mb-1 bg-amber-400 text-black text-xs font-mono font-bold uppercase tracking-wider py-1 px-2 rounded">
              Prizes Redeemed
            </div>
            <ReportRow label="Online" value={section.reports.online} onChange={(v) => updateReport("online", v)} testId={`${testIdPrefix}-r-online`} tab={tabOffset + 10} />
            <ReportRow label="Instant" value={section.reports.instant} onChange={(v) => updateReport("instant", v)} testId={`${testIdPrefix}-r-instant`} tab={tabOffset + 11} />
            <ReportRow label="Credit Card" value={section.reports.creditCard} onChange={(v) => updateReport("creditCard", v)} testId={`${testIdPrefix}-r-creditCard`} tab={tabOffset + 12} />
            <ReportRow label="Coupons/Others" value={section.reports.coupon} onChange={(v) => updateReport("coupon", v)} testId={`${testIdPrefix}-r-coupon`} tab={tabOffset + 13} />
          </div>
        </div>

        {/* Bottom totals row */}
        <div className="mt-4 pt-3 border-t border-border/60 grid grid-cols-2 gap-3">
          <div className="flex items-center justify-between bg-secondary/40 rounded px-2 py-1.5">
            <span className="font-mono text-xs font-bold text-card-foreground">Total Cash</span>
            <span className="font-mono text-sm font-bold text-primary" data-testid={`${testIdPrefix}-totalCash`}>
              {fmtMoney(totalCash)}
            </span>
          </div>
          <div
            className={`flex items-center justify-between rounded px-2 py-1.5 border ${
              mismatch ? "bg-red-500/15 border-red-500/50" : "bg-emerald-700/20 border-emerald-500/40"
            }`}
            data-testid={`${testIdPrefix}-cashInHand-wrap`}
          >
            <span className="font-mono text-xs font-bold text-card-foreground flex items-center gap-1">
              {mismatch ? <AlertTriangle className="w-3.5 h-3.5 text-red-400" /> : <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
              Cash In Hand
            </span>
            <span
              className={`font-mono text-sm font-bold ${mismatch ? "text-red-300" : "text-primary"}`}
              data-testid={`${testIdPrefix}-cashInHand`}
            >
              {fmtMoney(cashInHand)}
            </span>
          </div>
        </div>
        {mismatch && (
          <div
            className="mt-2 text-[11px] font-mono text-red-300 bg-red-500/10 border border-red-500/40 rounded px-2 py-1"
            data-testid={`${testIdPrefix}-mismatch-warning`}
          >
            Mismatch — Total Cash and Cash In Hand differ by {fmtMoney(Math.abs(totalCash - cashInHand))}.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReportRow({ label, value, onChange, testId, tab }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-center gap-1">
      <div className="text-card-foreground font-mono text-xs bg-emerald-800/40 rounded h-7 flex items-center px-2">
        {label}
      </div>
      <div className="relative">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-mono pointer-events-none">$</span>
        <Input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0"
          className="h-7 text-right text-sm font-mono pl-5 pr-1 rounded-md"
          data-testid={testId}
          data-lvm-tab={tab}
        />
      </div>
    </div>
  );
}

function ComputedRow({ label, value, testId, highlight }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] items-center gap-1">
      <div className={`font-mono text-xs rounded h-7 flex items-center px-2 ${highlight ? "bg-emerald-700/60 text-emerald-50 font-bold" : "bg-emerald-800/40 text-card-foreground"}`}>
        {label}
      </div>
      <div className="h-7 flex items-center justify-end pr-1 text-sm font-mono bg-secondary/30 rounded text-primary font-bold" data-testid={testId}>
        {fmtMoney(value)}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main LVM page: calendar nav + two sections + auto-save.
// ────────────────────────────────────────────────────────────────────────────
export default function LVM() {
  const { date: paramDate } = useParams();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(paramDate ? parseISO(paramDate) : new Date());
  const dateStr = format(selectedDate, "yyyy-MM-dd");
  const [activeTab, setActiveTab] = useState("entry");

  const [lvm1, setLvm1] = useState(EMPTY_SECTION);
  const [lvm2, setLvm2] = useState(EMPTY_SECTION);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(""); // '' | 'saving' | 'saved'
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  const isInitialLoad = useRef(true);
  const userHasEdited = useRef(false);
  const autoSaveTimer = useRef(null);

  useEffect(() => {
    navigate(`/lvm/${dateStr}`, { replace: true });
  }, [dateStr, navigate]);

  // Fetch on date change
  useEffect(() => {
    let cancelled = false;
    isInitialLoad.current = true;
    userHasEdited.current = false;
    setLoading(true);
    axios
      .get(`${API}/lvm/${dateStr}`)
      .then((res) => {
        if (cancelled) return;
        let l1 = { ...EMPTY_SECTION, ...res.data.lvm1, cash: { ...EMPTY_SECTION.cash, ...res.data.lvm1?.cash }, reports: { ...EMPTY_SECTION.reports, ...res.data.lvm1?.reports } };
        let l2 = { ...EMPTY_SECTION, ...res.data.lvm2, cash: { ...EMPTY_SECTION.cash, ...res.data.lvm2?.cash }, reports: { ...EMPTY_SECTION.reports, ...res.data.lvm2?.reports } };
        // Draft restore: prefer a local draft newer than the server copy
        // (recovers from tab crash / suspension before the auto-save fired).
        try {
          const raw = localStorage.getItem(`lvm_draft_${dateStr}`);
          if (raw) {
            const draft = JSON.parse(raw);
            const serverTs = res.data.updated_at ? Date.parse(res.data.updated_at) : 0;
            if (draft?.date === dateStr && Number(draft.ts) > serverTs && (draft.lvm1 || draft.lvm2)) {
              if (draft.lvm1) l1 = { ...EMPTY_SECTION, ...draft.lvm1, cash: { ...EMPTY_SECTION.cash, ...draft.lvm1.cash }, reports: { ...EMPTY_SECTION.reports, ...draft.lvm1.reports } };
              if (draft.lvm2) l2 = { ...EMPTY_SECTION, ...draft.lvm2, cash: { ...EMPTY_SECTION.cash, ...draft.lvm2.cash }, reports: { ...EMPTY_SECTION.reports, ...draft.lvm2.reports } };
              userHasEdited.current = true;
              toast.message("Restored unsaved LVM changes from your last session — saving now…");
            } else {
              localStorage.removeItem(`lvm_draft_${dateStr}`);
            }
          }
        } catch { /* localStorage unavailable — ignore */ }
        setLvm1(l1);
        setLvm2(l2);
      })
      .catch((err) => {
        if (!cancelled) toast.error(err.response?.data?.detail || "Failed to load LVM entry");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          // Allow auto-save to fire on subsequent edits
          setTimeout(() => { isInitialLoad.current = false; }, 200);
        }
      });
    return () => { cancelled = true; };
  }, [dateStr]);

  const markEdited = () => {
    if (!isInitialLoad.current) userHasEdited.current = true;
  };

  const handleLvm1Change = useCallback((next) => { markEdited(); setLvm1(next); }, []);
  const handleLvm2Change = useCallback((next) => { markEdited(); setLvm2(next); }, []);

  const saveNow = useCallback(async (silent = false) => {
    try {
      setSaveStatus("saving");
      await axios.put(`${API}/lvm/${dateStr}`, { lvm1, lvm2 });
      setSaveStatus("saved");
      // Server confirmed — safe to drop the local draft.
      try { localStorage.removeItem(`lvm_draft_${dateStr}`); } catch { /* ignore */ }
      // Bump refresh key so the date picker's "has data" highlight refreshes.
      setDataRefreshKey((k) => k + 1);
      if (!silent) toast.success("LVM saved");
      setTimeout(() => setSaveStatus(""), 1500);
    } catch (err) {
      setSaveStatus("");
      toast.error(err.response?.data?.detail || "Failed to save LVM");
    }
  }, [dateStr, lvm1, lvm2]);

  // Keep a snapshot of the freshest state for the flush-on-hide handler.
  const latestLvmRef = useRef({ lvm1, lvm2 });
  useEffect(() => { latestLvmRef.current = { lvm1, lvm2 }; }, [lvm1, lvm2]);

  // Persist an in-progress draft to localStorage on every edit — restored on
  // the next load if it's newer than the server copy.
  useEffect(() => {
    if (isInitialLoad.current || !userHasEdited.current) return;
    try {
      localStorage.setItem(`lvm_draft_${dateStr}`, JSON.stringify({ ts: Date.now(), date: dateStr, lvm1, lvm2 }));
    } catch { /* localStorage full or unavailable — silent */ }
  }, [dateStr, lvm1, lvm2]);

  // Flush pending edits via keepalive fetch the moment the tab is hidden —
  // background tabs throttle setTimeout so the debounced save may never fire.
  const flushRef = useRef(null);
  flushRef.current = () => {
    if (!userHasEdited.current) return;
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    keepaliveSave(`${API}/lvm/${dateStr}`, latestLvmRef.current);
    userHasEdited.current = false;
  };
  useFlushOnHide(flushRef);

  // Auto-save 800ms after any user edit (narrow window before tab switches)
  useEffect(() => {
    if (!userHasEdited.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveNow(true);
      userHasEdited.current = false;
    }, 800);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [lvm1, lvm2, saveNow]);

  const goPrev = () => setSelectedDate((d) => subDays(d, 1));
  const goNext = () => setSelectedDate((d) => addDays(d, 1));

  // Enter key advances focus to the next input in the LVM tab order.
  // Inputs across both sections carry data-lvm-tab numeric attrs (1..26).
  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const target = e.target;
    if (!target || target.tagName !== "INPUT") return;
    const cur = parseInt(target.getAttribute("data-lvm-tab") || "0", 10);
    if (!cur) return;
    e.preventDefault();
    const root = e.currentTarget;
    // Find the next tab number. If none, wrap to the first.
    const all = Array.from(root.querySelectorAll("[data-lvm-tab]"));
    if (!all.length) return;
    const sorted = all
      .map((el) => ({ el, n: parseInt(el.getAttribute("data-lvm-tab") || "0", 10) }))
      .filter((x) => x.n > 0)
      .sort((a, b) => a.n - b.n);
    const next = sorted.find((x) => x.n > cur) || sorted[0];
    if (next?.el) {
      next.el.focus();
      try { next.el.select && next.el.select(); } catch (_) { /* ignore */ }
    }
  };

  return (
    <div className="space-y-4" onKeyDown={handleKeyDown}>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 w-full max-w-sm" data-testid="lvm-tabs">
          <TabsTrigger value="entry" data-testid="lvm-tab-entry">Daily Lottery</TabsTrigger>
          <TabsTrigger value="dashboard" data-testid="lvm-tab-dashboard">Dashboard</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-4 mt-0">
          <LVMDashboard
            onJumpToDate={(d) => {
              setSelectedDate(parseISO(d));
              setActiveTab("entry");
            }}
          />
        </TabsContent>

        <TabsContent value="entry" className="space-y-4 mt-0">
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-mono text-2xl font-bold tracking-wider text-card-foreground mr-2" data-testid="lvm-title">LVM</h1>

            <Button variant="outline" size="icon" onClick={goPrev} className="btn-3d" data-testid="lvm-prev-day">
              <ChevronLeft className="w-4 h-4" />
            </Button>

            <DateWithDataPicker
              endpoint="/lvm/dates/range"
              selectedDate={selectedDate}
              onSelect={setSelectedDate}
              testId="lvm-date"
              refreshKey={dataRefreshKey}
            />

            <Button variant="outline" size="icon" onClick={goNext} className="btn-3d" data-testid="lvm-next-day">
              <ChevronRight className="w-4 h-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/print-lvm/${dateStr}`)}
              className="btn-3d"
              title="Print LVM report"
              data-testid="lvm-print-btn"
            >
              <Printer className="w-4 h-4 mr-1" />
              <span className="text-xs">Print</span>
            </Button>

            <ExportButton section="lvm" currentDate={dateStr} testIdPrefix="lvm-export" />

            <Button
              variant="outline"
              size="sm"
              onClick={() => saveNow(false)}
              disabled={saveStatus === "saving"}
              className="btn-3d"
              title="Save Now"
              data-testid="lvm-save-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              <span className="text-xs">{saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Saved" : "Save"}</span>
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-12 text-muted-foreground font-mono text-sm" data-testid="lvm-loading">
              Loading LVM entry…
            </div>
          ) : (
            <div className="flex flex-col xl:flex-row gap-4">
              <LVMSection
                title="LVM-1"
                section={lvm1}
                onChange={handleLvm1Change}
                dateStr={dateStr}
                testIdPrefix="lvm1"
                tabOffset={0}
              />
              <LVMSection
                title="LVM-2"
                section={lvm2}
                onChange={handleLvm2Change}
                dateStr={dateStr}
                testIdPrefix="lvm2"
                tabOffset={13}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
